/**
 * web/src/components/gates/EmailPreview.tsx
 * Editable email card for email-gate review. `from` is readonly (server-
 * determined per event); `to`, `cc`, `subject` are RHF-registered inputs.
 * The body is styled HTML (the backend fetches the template Doc via
 * `getDocHtml` so bold / italic / underline / links / lists survive
 * end-to-end), edited in the EmailBodyEditor rich-text editor (TipTap) so
 * the reviewer can see what's a link and add/edit links + formatting. The
 * HTML is the source of truth on the wire — on Approve the server splits a
 * `text/plain` fallback via `htmlToText` and sends both as a
 * multipart/alternative MIME message.
 *
 * Attachments (2026-06 testing-week feedback): the reviewer can attach files
 * below the body. Files are base64-encoded into the form's `attachments`
 * value and ride the approval edits; the server decodes + validates them
 * (email_step.parse_attachment_edits) and sends a multipart/mixed message.
 * Limits mirror the backend: 10 MB/file, 18 MB total, Gmail-blocked
 * extensions rejected up-front.
 *
 * Unfilled single-brace placeholders (e.g. `{Registration Deadline}`) are
 * listed in a yellow banner above the body so the reviewer can see at a
 * glance what still needs replacement.
 *
 * SECURITY (F24): body HTML sanitization (DOMPurify) lives in
 * EmailBodyEditor — both the controlled-prop seed and the edit read-back.
 *
 * Caller owns the <FormProvider /> with shape
 * `{ to, cc?, bcc?, subject, body, attachments? }`.
 */
import { useFormContext, Controller } from 'react-hook-form';
import { useMemo, useRef, useState, type ChangeEvent, type ReactNode } from 'react';
import { Input } from '../shared/Input';
import { cn } from '../../lib/cn';
import { Icon } from '../../lib/icons';
import { findPlaceholders, uniquePlaceholderKeys } from '../../lib/findPlaceholders';
import { EmailBodyEditor } from './EmailBodyEditor';

/**
 * One reviewer-attached file, base64-encoded for the approval edits. Mirrors
 * the backend's expected shape in `email_step.parse_attachment_edits`
 * (`sizeBytes` is display-only; the server measures the decoded bytes).
 */
export interface AttachmentDraft {
  filename: string;
  mimeType: string;
  sizeBytes: number;
  contentBase64: string;
}

// Mirror of the backend caps (email_step.MAX_ATTACHMENT_BYTES /
// MAX_TOTAL_ATTACHMENT_BYTES) so the reviewer gets instant feedback instead
// of a 409 on approve. Gmail's 25 MB limit applies to the base64-encoded
// MIME, hence the 18 MB raw total.
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
export const MAX_TOTAL_ATTACHMENT_BYTES = 18 * 1024 * 1024;

// Gmail-blocked file extensions (anti-malware policy) — mirror of the
// backend's _BLOCKED_EXTENSIONS so the picker rejects them up-front.
const BLOCKED_EXTENSIONS = new Set([
  'ade', 'adp', 'apk', 'appx', 'bat', 'cab', 'chm', 'cmd', 'com', 'cpl',
  'dll', 'dmg', 'exe', 'hta', 'img', 'ins', 'iso', 'isp', 'jar', 'js',
  'jse', 'lib', 'lnk', 'mde', 'mjs', 'msc', 'msi', 'msp', 'mst', 'nsh',
  'pif', 'ps1', 'scr', 'sct', 'shb', 'sys', 'vb', 'vbe', 'vbs', 'vhd',
  'vxd', 'wsc', 'wsf', 'wsh',
]);

/**
 * A file the SERVER attaches on send — shown in the preview as a
 * non-removable chip so the reviewer sees it will ride the email, but it is
 * not part of the form's `attachments` (the backend injects the bytes at
 * approve time). E.g. the promotion gate's monday-board speaker card.
 */
export interface PinnedAttachment {
  filename: string;
  /** Short provenance note rendered after the filename (e.g. "from the monday board"). */
  note?: string;
  /**
   * Authenticated image URL for a thumbnail preview of the file (e.g. the
   * promotion gate's speaker card at `/api/workflows/:id/card-candidates/
   * board_card`). When set the chip renders WITH the image so the reviewer can
   * see exactly what's attaching; a load failure falls back to the plain chip.
   */
  previewUrl?: string;
  /**
   * Set when the reviewer may take the file off the email (sponsor welcome
   * logos). Removed ids ride the approve edits as `removedPinnedAttachments`
   * and the server skips them at send.
   */
  fileId?: string;
  /** Link that opens (or downloads, for .ai/.eps) the full file. */
  openUrl?: string;
}

/**
 * Public props for EmailPreview. `from` is rendered as read-only text unless
 * the caller passes `fromSelector` (multi-account Gmail 2026-06: GateReview
 * renders a "Send from" dropdown there when the reviewer has more than one
 * connected account).
 */
export interface EmailPreviewProps {
  from: string;
  /** Optional control rendered in the From row INSTEAD of the plain text. */
  fromSelector?: ReactNode;
  /** Server-attached files pinned into the attachment list (not removable). */
  pinnedAttachments?: PinnedAttachment[];
  className?: string;
}

/**
 * Email gate review primitive. Pure presentation — submission is owned by
 * the parent GateReview's form handler. The body editor preserves HTML
 * styling on round-trip; the placeholder banner reads the live HTML and
 * surfaces any `{token}` text that still needs filling.
 *
 * @param from resolved sender address (rendered readonly)
 * @param fromSelector optional control replacing the plain-text From value
 * @param pinnedAttachments server-attached files shown as non-removable chips
 * @param className optional Tailwind classes for the outer card
 * @sideEffects calls form.register / Controller for to / cc / subject / body / attachments
 * @idempotent yes
 */
export function EmailPreview({ from, fromSelector, pinnedAttachments, className }: EmailPreviewProps) {
  const form = useFormContext<{
    to: string;
    cc?: string;
    bcc?: string;
    subject: string;
    body: string;
    attachments?: AttachmentDraft[];
    removedPinnedAttachments?: string[];
  }>();
  const errors = form.formState.errors;
  const removedPinned = form.watch('removedPinnedAttachments');

  // Live-watch the body (HTML) so the warning banner refreshes as the
  // reviewer edits. The placeholder regex matches `{token}` against text
  // content — tags don't contain `{`/`}` so running it on raw HTML is safe.
  const body = form.watch('body') ?? '';
  const uniqueKeys = useMemo(() => uniquePlaceholderKeys(findPlaceholders(body)), [body]);
  const hasPlaceholders = uniqueKeys.length > 0;

  return (
    <div className={cn('border border-border rounded-lg overflow-hidden bg-bg', className)}>
      <div className="bg-bg-soft border-b border-border p-4 flex flex-col gap-2.5">
        <Row label="From">
          {fromSelector ?? <span className="text-text-muted">{from}</span>}
        </Row>
        <Row label="To">
          <Input
            id="email-to"
            {...form.register('to', { required: 'Recipient is required' })}
            aria-invalid={errors.to ? true : undefined}
          />
        </Row>
        <Row label="CC">
          <Input id="email-cc" {...form.register('cc')} placeholder="Optional" />
        </Row>
        <Row label="BCC">
          <Input
            id="email-bcc"
            {...form.register('bcc')}
            placeholder="Hidden recipients (optional)"
          />
        </Row>
        <Row label="Subject">
          <Input
            id="email-subject"
            {...form.register('subject', { required: 'Subject is required' })}
            aria-invalid={errors.subject ? true : undefined}
          />
        </Row>
      </div>
      {hasPlaceholders && (
        <div
          role="alert"
          aria-live="polite"
          className="flex items-start gap-2.5 px-5 py-3 bg-waiting-soft border-b border-waiting-border text-waiting"
        >
          <Icon name="alert" size={14} className="mt-0.5 shrink-0" />
          <div className="text-[12px] leading-[1.55] min-w-0">
            <div className="font-semibold mb-1">
              {uniqueKeys.length} unfilled placeholder
              {uniqueKeys.length === 1 ? '' : 's'} in this email
            </div>
            <div className="flex flex-wrap gap-1.5">
              {uniqueKeys.map((k) => (
                <span
                  key={k}
                  className="inline-flex items-center gap-1 rounded-sm border border-waiting-border bg-bg px-1.5 py-0.5 font-mono text-[11px] text-waiting"
                >
                  {'{'}
                  {k}
                  {'}'}
                </span>
              ))}
            </div>
            <div className="mt-1 text-text-muted">
              Replace each placeholder before sending — the recipient will see them verbatim
              otherwise.
            </div>
          </div>
        </div>
      )}
      <div className="p-5">
        <Controller
          control={form.control}
          name="body"
          rules={{
            required: 'Body is required',
            validate: (v) => hasText(typeof v === 'string' ? v : '') || 'Body is required',
          }}
          render={({ field, fieldState }) => (
            <EmailBodyEditor
              html={typeof field.value === 'string' ? field.value : ''}
              onChange={field.onChange}
              ariaInvalid={fieldState.error ? true : undefined}
            />
          )}
        />
      </div>
      <div className="px-5 pb-5">
        <Controller
          control={form.control}
          name="attachments"
          render={({ field }) => (
            <AttachmentPicker
              attachments={Array.isArray(field.value) ? field.value : []}
              pinned={pinnedAttachments ?? []}
              removedPinned={Array.isArray(removedPinned) ? removedPinned : []}
              onRemovedPinnedChange={(next) =>
                form.setValue('removedPinnedAttachments', next, { shouldDirty: true })
              }
              onChange={field.onChange}
            />
          )}
        />
      </div>
    </div>
  );
}

/**
 * Internal: file picker + chip list for email attachments. Reads picked files
 * to base64 (FileReader), validates against the mirrored backend limits, and
 * writes `AttachmentDraft[]` to the form. Rejected files surface in an inline
 * error line; accepted files render as removable chips. `pinned` files render
 * FIRST — they are server-attached at send time and never enter the form
 * value. A pinned file with a `fileId` can be taken off (and put back): its id
 * goes in `removedPinned`, which the parent writes to the form.
 */
function AttachmentPicker({
  attachments,
  pinned,
  removedPinned,
  onRemovedPinnedChange,
  onChange,
}: {
  attachments: AttachmentDraft[];
  pinned: PinnedAttachment[];
  removedPinned: string[];
  onRemovedPinnedChange: (next: string[]) => void;
  onChange: (next: AttachmentDraft[]) => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  const totalBytes = attachments.reduce((sum, a) => sum + a.sizeBytes, 0);

  const handlePick = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    // Reset so picking the same file again re-fires the change event.
    e.target.value = '';
    if (files.length === 0) return;

    const rejected: string[] = [];
    const accepted: AttachmentDraft[] = [];
    let runningTotal = totalBytes;
    for (const file of files) {
      const ext = file.name.includes('.') ? file.name.split('.').pop()!.toLowerCase() : '';
      if (BLOCKED_EXTENSIONS.has(ext)) {
        rejected.push(`${file.name} (.${ext} files are blocked by Gmail — zip it instead)`);
        continue;
      }
      if (file.size > MAX_ATTACHMENT_BYTES) {
        rejected.push(`${file.name} (${formatBytes(file.size)} — over the 10 MB per-file limit)`);
        continue;
      }
      if (runningTotal + file.size > MAX_TOTAL_ATTACHMENT_BYTES) {
        rejected.push(`${file.name} (would push attachments over the 18 MB total limit)`);
        continue;
      }
      try {
        const contentBase64 = await fileToBase64(file);
        runningTotal += file.size;
        accepted.push({
          filename: file.name,
          mimeType: file.type || 'application/octet-stream',
          sizeBytes: file.size,
          contentBase64,
        });
      } catch {
        rejected.push(`${file.name} (couldn't read the file — try again)`);
      }
    }
    if (accepted.length > 0) onChange([...attachments, ...accepted]);
    setError(rejected.length > 0 ? `Not attached: ${rejected.join('; ')}` : null);
  };

  const remove = (index: number) => {
    onChange(attachments.filter((_, i) => i !== index));
    setError(null);
  };

  return (
    <div className="border-t border-border pt-3 flex flex-col gap-2">
      {(pinned.length > 0 || attachments.length > 0) && (
        <ul className="flex flex-wrap items-start gap-1.5 m-0 p-0 list-none">
          {pinned.map((p) => {
            const fileId = p.fileId;
            const removed = fileId !== undefined && removedPinned.includes(fileId);
            return (
              <PinnedAttachmentChip
                key={`pinned-${fileId ?? p.filename}`}
                pinned={p}
                removed={removed}
                onToggle={
                  fileId === undefined
                    ? undefined
                    : () =>
                        onRemovedPinnedChange(
                          removed
                            ? removedPinned.filter((id) => id !== fileId)
                            : [...removedPinned, fileId],
                        )
                }
              />
            );
          })}
          {attachments.map((a, i) => (
            <li
              key={`${a.filename}-${i}`}
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-bg-soft pl-2 pr-1 py-1 text-[12px] text-text max-w-full"
            >
              <Icon name="paperclip" size={12} className="shrink-0 text-text-muted" />
              <span className="truncate max-w-[260px]" title={a.filename}>
                {a.filename}
              </span>
              <span className="text-text-muted shrink-0">{formatBytes(a.sizeBytes)}</span>
              <button
                type="button"
                aria-label={`Remove attachment ${a.filename}`}
                onClick={() => remove(i)}
                className="inline-flex items-center justify-center h-5 w-5 rounded-sm text-text-muted hover:text-text hover:bg-bg transition-colors"
              >
                <Icon name="x" size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-center gap-2.5">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-md border border-border bg-bg px-2.5 py-1.5',
            'text-[12px] font-medium text-text-muted hover:text-text hover:bg-bg-soft transition-colors',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-1',
          )}
        >
          <Icon name="paperclip" size={13} />
          Attach files
        </button>
        {attachments.length > 0 && (
          <span className="text-[11px] text-text-muted">
            {attachments.length} file{attachments.length === 1 ? '' : 's'} ·{' '}
            {formatBytes(totalBytes)} of 18 MB
          </span>
        )}
        <input
          ref={inputRef}
          type="file"
          multiple
          onChange={(e) => void handlePick(e)}
          className="hidden"
          aria-hidden
          tabIndex={-1}
        />
      </div>
      {error && (
        <p role="alert" className="text-[12px] text-failed m-0">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * One server-attached (pinned) file in the attachment list. With a
 * `previewUrl` it renders a thumbnail of the image beside the filename so the
 * reviewer sees exactly what will attach (e.g. the promotion gate's speaker
 * card or a sponsor's logo); the thumbnail links to the full-size file. If the
 * image can't load it falls back to the plain paperclip chip. `openUrl` adds an
 * Open link (files with no thumbnail, like .ai/.eps, download). `onToggle`
 * adds a remove button; a removed chip stays visible, struck through, with
 * Undo, so taking a logo off is never a dead end.
 */
function PinnedAttachmentChip({
  pinned,
  removed = false,
  onToggle,
}: {
  pinned: PinnedAttachment;
  removed?: boolean;
  onToggle?: () => void;
}) {
  const [imgOk, setImgOk] = useState(true);
  const showThumb = Boolean(pinned.previewUrl) && imgOk && !removed;

  return (
    <li
      className={cn(
        'inline-flex items-center gap-2.5 rounded-md border border-border bg-bg-soft text-[12px] text-text max-w-full',
        showThumb ? 'p-1.5 pr-1.5' : 'px-2 py-1 pr-1',
        removed && 'opacity-60',
      )}
    >
      {showThumb && (
        <a
          href={pinned.openUrl ?? pinned.previewUrl}
          target="_blank"
          rel="noreferrer"
          title="Open the full-size file"
          className="shrink-0 rounded overflow-hidden border border-border bg-[repeating-conic-gradient(#e5e5e5_0%_25%,#fff_0%_50%)] bg-[length:12px_12px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-1"
        >
          <img
            src={pinned.previewUrl}
            alt={`Preview of ${pinned.filename}`}
            onError={() => setImgOk(false)}
            className="block h-16 w-auto max-w-[128px] object-contain"
          />
        </a>
      )}
      <span className="min-w-0 flex flex-col gap-0.5">
        <span className="inline-flex items-center gap-1.5">
          <Icon name="paperclip" size={12} className="shrink-0 text-text-muted" />
          <span className={cn('truncate max-w-[220px]', removed && 'line-through')} title={pinned.filename}>
            {pinned.filename}
          </span>
        </span>
        <span className="text-[11px] text-text-muted">
          {removed ? 'removed — won\u2019t be attached' : pinned.note}
          {pinned.openUrl && !removed && (
            <>
              {pinned.note ? ' · ' : ''}
              <a
                href={pinned.openUrl}
                target="_blank"
                rel="noreferrer"
                className="text-accent hover:underline"
              >
                {showThumb ? 'Open' : 'Download'}
              </a>
            </>
          )}
        </span>
      </span>
      {onToggle && (
        removed ? (
          <button
            type="button"
            onClick={onToggle}
            className="shrink-0 rounded-sm px-1.5 py-0.5 text-[11.5px] font-medium text-accent hover:bg-bg transition-colors"
          >
            Undo
          </button>
        ) : (
          <button
            type="button"
            aria-label={`Remove attachment ${pinned.filename}`}
            title="Don't attach this file"
            onClick={onToggle}
            className="shrink-0 self-start inline-flex items-center justify-center h-5 w-5 rounded-sm text-text-muted hover:text-text hover:bg-bg transition-colors"
          >
            <Icon name="x" size={12} />
          </button>
        )
      )}
    </li>
  );
}

/**
 * Reads a File to its base64 content (no data: URL prefix) — the shape the
 * backend's parse_attachment_edits decodes.
 *
 * @param file picked browser File
 * @returns promise of the bare base64 string
 * @sideEffects none beyond reading the file
 * @idempotent yes
 */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? '');
      const comma = result.indexOf(',');
      if (comma === -1) {
        reject(new Error('unexpected FileReader result'));
        return;
      }
      resolve(result.slice(comma + 1));
    };
    reader.onerror = () => reject(reader.error ?? new Error('file read failed'));
    reader.readAsDataURL(file);
  });
}

/**
 * Human-readable file size ("843 KB", "2.4 MB").
 *
 * @param bytes raw byte count
 * @returns formatted size string
 * @sideEffects none
 * @idempotent yes
 */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Returns true if the HTML body has any visible text content. Used to
 * reject empty-body submissions (a rich-text editor can technically hold
 * `<br>` / `<p></p>` and look "empty" while having non-empty HTML).
 *
 * @param html HTML body string (possibly empty or just stray tags)
 * @returns true when at least one non-whitespace character of text exists
 * @sideEffects none
 * @idempotent yes
 */
function hasText(html: string): boolean {
  return (
    html
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim().length > 0
  );
}

/**
 * Internal row layout — fixed-width label + flex input. Matches the
 * `.email-row` grid in styles.css.
 */
function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[64px_1fr] items-center gap-2.5 text-[12px]">
      <span className="font-medium text-text-subtle">{label}</span>
      <div>{children}</div>
    </div>
  );
}
